import { uiText, useUiLanguage } from '../utils/uiTranslation';
/**
 * ═══════════════════════════════════════════════════════════════
 * FEATURE ACCESS HOOK
 * ═══════════════════════════════════════════════════════════════
 * 
 * Provides granular feature-level access control based on user's plan tier.
 * 
 * Usage:
 *   const { canBulkUpload, canUseAIContracts, hasFeature } = useFeatureAccess();
 * 
 * Returns:
 *   loading: true while checking access
 *   planId: current plan ID (e.g., 'ats_small', 'bundle_large')
 *   productTypes: array of product types user has access to
 *   currentPlan: current plan object with features
 *   hasFeature: function to check if user has a specific feature
 *   getRequiredPlanForFeature: function to get the minimum plan needed for a feature
 *   canBulkUpload: boolean for bulk CV upload access
 *   canUseAIContracts: boolean for AI contract generation access
 *   canUseOnboardingAutomations: boolean for onboarding automation access
 *   canUseAutoAssessments: boolean for auto assessment access
 *   canUseAIKnowledgeBase: boolean for AI knowledge base access
 */

import { useAccessControl } from './useAccessControl';
import { ATS_PLANS, TSE_PLANS, BUNDLE_PLANS } from '../utils/featureFlags';

export const useFeatureAccess = () => {
  useUiLanguage();
  const { planId, productTypes, loading } = useAccessControl();
  
  /**
   * Get the current plan object based on planId
   */
  const getCurrentPlan = () => {
    if (!planId) return null;
    
    // Priority: Check bundle first (if user has bundle, they get bundle features)
    // Then check ATS, then TSE
    if (productTypes.includes('bundle') && BUNDLE_PLANS[planId]) {
      return BUNDLE_PLANS[planId];
    }
    
    if (productTypes.includes('ats') && ATS_PLANS[planId]) {
      return ATS_PLANS[planId];
    }
    
    if (productTypes.includes('tse') && TSE_PLANS[planId]) {
      return TSE_PLANS[planId];
    }
    
    return null;
  };
  
  /**
   * Check if user has a specific feature
   * @param {string} featureName - Feature key from plan's features object
   * @returns {boolean} - True if user has access to the feature
   */
  const hasFeature = (featureName) => {
    if (!planId) {
      console.warn('[useFeatureAccess] No planId available');
      return false;
    }
    
    const plan = getCurrentPlan();
    if (!plan) {
      console.warn('[useFeatureAccess] Plan not found for planId:', planId, 'productTypes:', productTypes);
      return false;
    }
    
    // FIRST: Check if feature exists directly in the plan's features object
    // This works for both ATS plans and bundle plans (which now have explicit features)
    if (plan.features?.[featureName] === true) {
      return true;
    }
    
    // SECOND: For bundle plans, ALWAYS check the included ATS plan features
    // Bundle plans inherit ALL features from their corresponding ATS tier
    if (productTypes.includes('bundle')) {
      // Try plan.includes.ats first
      if (plan.includes?.ats) {
        const atsPlan = ATS_PLANS[plan.includes.ats];
        if (atsPlan?.features?.[featureName] === true) {
          return true;
        }
      }
      // Also try deriving from planId (bundle_large -> ats_large)
      if (planId.startsWith('bundle_')) {
        const atsPlanId = planId.replace('bundle_', 'ats_');
        const atsPlan = ATS_PLANS[atsPlanId];
        if (atsPlan?.features?.[featureName] === true) {
          return true;
        }
      }
    }
    
    // THIRD: For ATS plans, check directly
    if (productTypes.includes('ats') && ATS_PLANS[planId]) {
      const atsPlan = ATS_PLANS[planId];
      if (atsPlan?.features?.[featureName] === true) {
        return true;
      }
    }
    
    return false;
  };
  
  /**
   * Get the minimum required plan for a feature
   * @param {string} featureName - Feature key to check
   * @param {string} productType - Product type to search in ('ats', 'tse', 'bundle')
   * @returns {object|null} - { planId, plan } or null
   */
  const getRequiredPlanForFeature = (featureName, productType = 'ats') => {
    const plans = productType === 'ats' ? ATS_PLANS : 
                  productType === 'tse' ? TSE_PLANS : BUNDLE_PLANS;
    
    // Iterate through plans to find the first (typically cheapest) that has the feature
    for (const [planId, plan] of Object.entries(plans)) {
      if (plan.features?.[featureName] === true) {
        return { planId, plan };
      }
    }
    
    return null;
  };
  
  /**
   * Get upgrade message for a locked feature
   * @param {string} featureName - Feature key
   * @returns {string} - Human-readable upgrade message
   */
  const getUpgradeMessage = (featureName) => {
    const currentPlan = getCurrentPlan();
    const requiredPlan = getRequiredPlanForFeature(featureName, productTypes[0]);
    
    if (!requiredPlan) {
      return uiText('This feature is not available in any plan.');
    }
    
    if (currentPlan) {
      return uiText('Upgrade to {{plan}} or higher to unlock this feature.', { plan: requiredPlan.plan.name });
    }
    
    return uiText('Subscribe to {{plan}} or higher to unlock this feature.', { plan: requiredPlan.plan.name });
  };
  
  // Get current plan
  const currentPlan = getCurrentPlan();
  
  return {
    loading,
    planId,
    productTypes,
    currentPlan,
    
    // Core functions
    hasFeature,
    getRequiredPlanForFeature,
    getUpgradeMessage,
    
    // Specific feature checks for convenience
    canBulkUpload: hasFeature('bulkUpload'),
    canUseAIContracts: hasFeature('aiContracts'),
    canUseOnboardingAutomations: hasFeature('onboardingAutomations'),
    canUseAutoAssessments: hasFeature('autoAssessments'),
    canUseAIKnowledgeBase: hasFeature('aiKnowledgeBase'),
    canViewAnalytics: hasFeature('analytics'),
  };
};

export default useFeatureAccess;

